import React, { useMemo } from 'react';

export interface ViewportContainerProps {
  /**
   * Width of the viewport window
   */
  width: number | string;

  /**
   * Height of the viewport window
   */
  height: number | string;

  /**
   * Position X of the viewport window
   */
  x?: number | string;

  /**
   * Position Y of the viewport window
   */
  y?: number | string;

  /**
   * ID for the viewport clip path
   */
  viewportId?: string;

  /**
   * CSS class name for styling
   */
  className?: string;

  /**
   * ID for the viewport container element
   */
  id?: string;

  /**
   * Children to render inside the viewport
   */
  children: React.ReactNode;
}

/**
 * ViewportContainer component
 * Creates a fixed-size viewport window through which to view its children content
 * Acts as a "window" into the SVG content with its own clipping region
 */
const ViewportContainer: React.FC<ViewportContainerProps> = ({
  width,
  height,
  x = 0,
  y = 0,
  viewportId = 'viewport-clip',
  className = '',
  id,
  children
}) => {
  // Generate a unique ID for this viewport instance using React's useId
  const reactId = React.useId();
  const uniqueViewportId = useMemo(() => 
    `${viewportId}-${reactId.replace(/:/g, '-')}`, 
    [viewportId, reactId]
  );
  
  // Dimensions calculation removed as clipPath and border rect were removed
  
  return (
    <g
      id={id}
      className={`viewport-content ${className}`}
      transform={`translate(${x},${y})`}
      data-viewport-id={uniqueViewportId}
    >
      {/* Simplified: removed clipPath and border rect as they add unnecessary complexity */}
      {/* Chart elements are positioned within bounds, clipping not needed */}
      {children}
    </g>
  );
};

export default ViewportContainer;
